import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { TeamNewFinStatusReasonTagID } from '../../../../../../../main/teams/teamsTypes';

import {
  UniversalFilterCompanyFinCategoriesValue,
  UniversalFilterCompanyFinCategoriesModes,
  UniversalFilterCompanyFinCategoriesFilterState,
  UniversalFilterCompanyFinCategoriesOnFilterStateChange,
  UniversalFilterCompanyFinCategoriesOnChange
} from '../../UniversalFilterCompanyFinCategories.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCompanyFinCategoriesFilterState } from '../useUniversalFilterCompanyFinCategoriesFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterCompanyFinCategoriesModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterCompanyFinCategoriesModes>[] =
  [
    {
      value: UniversalFilterCompanyFinCategoriesModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterCompanyFinCategoriesModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterCompanyFinCategoriesOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCompanyFinCategoriesValue;
  onChange: UniversalFilterCompanyFinCategoriesOnChange;
}

function useUniversalFilterCompanyFinCategories({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCompanyFinCategoriesOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCompanyFinCategoriesFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCompanyFinCategoriesFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCompanyFinCategoriesFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          categoryIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCompanyFinCategoriesOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterCompanyFinCategoriesModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        categoryIds: filterState.categoryIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleCategoryIdsChange = useCallback<
    (categoryIds: TeamNewFinStatusReasonTagID[]) => void
  >(
    (categoryIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        categoryIds: categoryIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleCategoryIdsChange
  };
}

export default useUniversalFilterCompanyFinCategories;
